// ==================== HEADER COMPONENT (Public Layout) ====================

import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LogIn, ArrowRight, ChevronDown, LayoutDashboard, LogOut } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Logo } from '../ui/Logo';
import { Button } from '../ui/Button';
import { ROUTES } from '../../constants';
import { cn } from '../../utils/helpers';
import { useAuth } from '../../contexts';

const navLinks = [
  { label: 'Features', href: ROUTES.FEATURES },
  { label: 'Pricing', href: ROUTES.PRICING },
  { label: 'About', href: ROUTES.ABOUT },
  { label: 'Contact', href: ROUTES.CONTACT },
];

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (href: string) => location.pathname === href;

  // Get the dashboard route based on user role
  const getDashboardRoute = () => {
    if (!user) return ROUTES.AUTH.LOGIN;
    return user.role === 'SUPER_ADMIN' ? ROUTES.ADMIN.DASHBOARD : ROUTES.CUSTOMER.DASHBOARD;
  };

  // Get user display name
  const getUserDisplayName = () => {
    if (!user) return '';
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.firstName || user.email?.split('@')[0] || 'User';
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user) return '';
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    return firstName[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U';
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        isScrolled
          ? 'bg-(--color-midnight-navy)/95 backdrop-blur-lg shadow-lg shadow-black/20'
          : 'bg-transparent'
      )}
    >
      {/* Top accent line */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-linear-to-r from-transparent via-(--color-signal-orange) to-transparent opacity-60" />

      <div className="container-custom">
        <div className="flex items-center justify-between h-[4.5rem] lg:h-20">
          {/* Logo */}
          <Link to={ROUTES.HOME} className="shrink-0 relative group">
            <Logo size="lg" />
            {/* Subtle glow on hover */}
            <div className="absolute inset-0 bg-(--color-signal-orange)/0 group-hover:bg-(--color-signal-orange)/10 rounded-lg transition-all duration-300 -z-10" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center">
            <div className="flex items-center gap-1 px-2 py-1.5 rounded-full bg-(--color-charcoal-black)/50 border border-(--color-border-gray)/30">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={cn(
                    'relative px-4 py-2 text-sm font-medium rounded-full transition-all duration-200',
                    isActive(link.href)
                      ? 'text-white bg-(--color-signal-orange)/20'
                      : 'text-(--color-soft-gray) hover:text-white hover:bg-white/5'
                  )}
                >
                  {link.label}
                  {isActive(link.href) && (
                    <motion.div
                      layoutId="activeNav"
                      className="absolute inset-0 rounded-full bg-(--color-signal-orange)/10 border border-(--color-signal-orange)/30"
                      transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                </Link>
              ))}
            </div>
          </nav>

          {/* Desktop Auth Buttons / User Menu */}
          <div className="hidden lg:flex items-center gap-3">
            {isAuthenticated && user ? (
              <>
                {/* User Avatar & Name */}
                <Link 
                  to={getDashboardRoute()}
                  className="flex items-center gap-3 px-3 py-1.5 rounded-full bg-(--color-charcoal-black)/50 
                             border border-(--color-border-gray)/30 hover:border-(--color-signal-orange)/50 
                             transition-all duration-200 group"
                >
                  <div className="w-8 h-8 rounded-full bg-linear-to-br from-(--color-signal-orange) to-(--color-orange-light) 
                                  flex items-center justify-center shadow-md shadow-(--color-signal-orange)/20">
                    <span className="text-xs font-semibold text-white">{getUserInitials()}</span>
                  </div>
                  <span className="text-sm font-medium text-(--color-soft-gray) group-hover:text-white transition-colors">
                    {getUserDisplayName()}
                  </span>
                </Link>

                {/* Dashboard Button */}
                <Link to={getDashboardRoute()}>
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button 
                      variant="primary" 
                      size="sm"
                      leftIcon={<LayoutDashboard size={16} />}
                      className="shadow-lg shadow-(--color-signal-orange)/20"
                    >
                      Dashboard
                    </Button>
                  </motion.div>
                </Link>
              </>
            ) : (
              <>
                <Link to={ROUTES.AUTH.LOGIN}>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    leftIcon={<LogIn size={16} />}
                    className="text-(--color-soft-gray) hover:text-white hover:bg-white/5 border border-transparent hover:border-(--color-border-gray)/50"
                  >
                    Sign In
                  </Button>
                </Link>
                <Link to={ROUTES.AUTH.REGISTER}>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button 
                      variant="primary" 
                      size="sm"
                      rightIcon={<ArrowRight size={16} />}
                      className="shadow-lg shadow-(--color-signal-orange)/20"
                    >
                      Get Started
                    </Button>
                  </motion.div>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            whileTap={{ scale: 0.95 }}
            className={cn(
              'lg:hidden p-2.5 rounded-lg transition-all duration-200',
              isMobileMenuOpen
                ? 'bg-(--color-signal-orange)/20 text-(--color-signal-orange)'
                : 'text-(--color-soft-gray) hover:text-white hover:bg-white/5'
            )}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={isMobileMenuOpen ? 'close' : 'menu'}
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </motion.div>
            </AnimatePresence>
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="lg:hidden overflow-hidden bg-(--color-midnight-navy)/98 backdrop-blur-lg border-t border-(--color-border-gray)/30"
          >
            <div className="container-custom py-6">
              <nav className="flex flex-col gap-2">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.href}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={link.href}
                      className={cn(
                        'flex items-center justify-between px-4 py-3 rounded-lg text-base font-medium transition-all duration-200',
                        isActive(link.href)
                          ? 'text-(--color-signal-orange) bg-(--color-signal-orange)/10'
                          : 'text-(--color-soft-gray) hover:text-white hover:bg-white/5'
                      )}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {link.label}
                      <ChevronDown size={16} className="rotate-[-90deg] opacity-50" />
                    </Link>
                  </motion.div>
                ))}
                
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="flex flex-col gap-3 pt-4 mt-2 border-t border-(--color-border-gray)/30"
                >
                  {isAuthenticated && user ? (
                    <>
                      {/* User Info */}
                      <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-(--color-charcoal-black)/50 border border-(--color-border-gray)/30">
                        <div className="w-10 h-10 rounded-full bg-linear-to-br from-(--color-signal-orange) to-(--color-orange-light) 
                                        flex items-center justify-center shadow-md shadow-(--color-signal-orange)/20">
                          <span className="text-sm font-semibold text-white">{getUserInitials()}</span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-white">{getUserDisplayName()}</span>
                          <span className="text-xs text-(--color-muted-gray)">{user.email}</span>
                        </div>
                      </div>
                      
                      <Link to={getDashboardRoute()} onClick={() => setIsMobileMenuOpen(false)}>
                        <Button 
                          variant="primary" 
                          fullWidth
                          leftIcon={<LayoutDashboard size={18} />}
                        >
                          Go to Dashboard
                        </Button>
                      </Link>
                      
                      <Button 
                        variant="secondary" 
                        fullWidth
                        leftIcon={<LogOut size={18} />}
                        onClick={() => {
                          setIsMobileMenuOpen(false);
                          logout();
                        }}
                      >
                        Sign Out
                      </Button>
                    </>
                  ) : (
                    <>
                      <Link to={ROUTES.AUTH.LOGIN} onClick={() => setIsMobileMenuOpen(false)}>
                        <Button 
                          variant="secondary" 
                          fullWidth
                          leftIcon={<LogIn size={18} />}
                        >
                          Sign In
                        </Button>
                      </Link>
                      <Link to={ROUTES.AUTH.REGISTER} onClick={() => setIsMobileMenuOpen(false)}>
                        <Button 
                          variant="primary" 
                          fullWidth
                          rightIcon={<ArrowRight size={18} />}
                        >
                          Get Started
                        </Button>
                      </Link>
                    </>
                  )}
                </motion.div>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export { Header };
